import { motion } from 'framer-motion';
import { Suspense, useEffect, useMemo } from 'react';
import { rem } from '@visiba/std';
import styled from 'styled-components';
import { useService } from '@visiba/instantiation';
import { SvgHttpService } from '@services/svg_http.service';
import { sanitizedInjectHTML } from '@visiba-cortex/react-ui-std';
import { ConcurrentPresentation, Presentation, usePresentation } from '@visiba/presentation';
import { Icon } from './icon.component';
import { ErrorBoundary } from './error_boundary.component';

interface Props {
	imgSrc: string;
	size: number;
	overrideColor?: string;
}

interface PresentationModel {
	unsafeSvgString: string;
}

const elements = {
	container: styled.div<{ size: number; overrideColor?: string }>`
		display: flex;
		justify-content: center;
		align-items: center;
		width: ${({ size }) => rem(size)};
		height: ${({ size }) => rem(size)};
		font-size: ${({ size }) => rem(size)};

		${({ overrideColor }) => {
			if (!overrideColor) return null;

			return `
				color: ${overrideColor};
			`;
		}}

		& svg {
			width: 1em;
			height: 1em;
			display: inline-block;
			vertical-align: text-top;
			pointer-events: none;
			fill: currentColor;
		}
	`,
	svgContent: styled(motion.div)`
		display: flex;
		width: 100%;
		height: 100%;
	`,
};

const REGEX_TO_REMOVE_RANDOM_COLOR_MODIFIERS_IN_A_SVG = /(fill|class|style)="(.*?)"/g;

function Inner({ presentation }: { presentation: ConcurrentPresentation<PresentationModel> }): JSX.Element {
	const { unsafeSvgString } = usePresentation(presentation);
	const parsedUnsafeSvgString = unsafeSvgString.replace(REGEX_TO_REMOVE_RANDOM_COLOR_MODIFIERS_IN_A_SVG, '');

	if (parsedUnsafeSvgString === '') {
		return (
			<elements.svgContent data-testid='external_svg_inner' animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
				{/* TODO: this should be changed to some defaultish icon, we don't have that yet. [X] like this or something */}
				<Icon type='rocket' data-testId='external_svg_error' />
			</elements.svgContent>
		);
	}

	return (
		<elements.svgContent
			data-testid='external_svg_inner'
			animate={{ opacity: 1 }}
			initial={{ opacity: 0 }}
			dangerouslySetInnerHTML={sanitizedInjectHTML(parsedUnsafeSvgString)}
		/>
	);
}

export function ExternalSvg({ imgSrc, size, overrideColor }: Props): JSX.Element {
	const visibaImageHttpClientService = useService(SvgHttpService);
	const presentation = useMemo(() => Presentation.createConcurrent<PresentationModel>(), []);

	useEffect(() => {
		presentation
			.suspend(visibaImageHttpClientService.get(imgSrc), (unsafeSvgString) => ({ unsafeSvgString }))
			.gracefulDegradation(async () => {
				// TODO: update the Presentation api in some sense.
				// This is typed to return T but it's still being ran through the map
				// function, breaking things.
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				return '' as any;
			});
	}, [imgSrc, presentation, visibaImageHttpClientService]);

	return (
		<elements.container size={size} overrideColor={overrideColor}>
			<ErrorBoundary fallback={<elements.svgContent />}>
				<Suspense fallback={<elements.svgContent />}>
					<Inner presentation={presentation} />
				</Suspense>
			</ErrorBoundary>
		</elements.container>
	);
}
